import type { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";

/**
 * Per-tab memory of whether the operator chose to be online for voice.
 *
 * Backed by `sessionStorage` (not `localStorage`) on purpose: the flag is
 * scoped to a single browser tab and survives a refresh, but a brand-new
 * tab starts offline. That keeps "Available" sticky across reloads without
 * a fresh tab silently inheriting availability.
 *
 * The flag is keyed by acting identity (`targetMongoUserId`) so switching
 * who the operator is acting as doesn't inherit the previous identity's
 * online state.
 */
const KEY_PREFIX = "support-v2:voice-desired-online";

const storageKey = (targetMongoUserId: MongoUserId | undefined): string =>
  `${KEY_PREFIX}:${targetMongoUserId ?? "self"}`;

const defaultStore = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    // Storage access throws when cookies/storage are blocked.
    return null;
  }
};

export const readVoiceDesiredOnline = (
  targetMongoUserId: MongoUserId | undefined,
  store: Storage | null = defaultStore(),
): boolean => {
  try {
    return store?.getItem(storageKey(targetMongoUserId)) === "1";
  } catch {
    return false;
  }
};

export const writeVoiceDesiredOnline = (
  targetMongoUserId: MongoUserId | undefined,
  online: boolean,
  store: Storage | null = defaultStore(),
): void => {
  try {
    const key = storageKey(targetMongoUserId);
    if (online) {
      store?.setItem(key, "1");
    } else {
      store?.removeItem(key);
    }
  } catch {
    // Degrade to in-memory-only — the pre-persistence behavior.
  }
};
